import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { WebsiteSettings } from '@app/core/interfaces/website-settings.interface';
import { SessionStorageService } from '@app/shared/services/storage';
import { MediaInterface } from '@core/interfaces/media.interface';
import { MetaTagsInfoInterface } from '@core/interfaces/meta-tags-info.interface';
import { Page } from '@core/interfaces/page.interface';
import { PageHeaderScriptService } from './page-header-script.service';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private readonly jsonLdMarkerClass = 'app-json-ld';

  constructor(
    private titleService: Title,
    private meta: Meta,
    private storageService: SessionStorageService,
    private pageHeaderScriptService: PageHeaderScriptService,
    @Inject(DOCUMENT) private _document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
  }

  private _subpageTitle = '';

  get subpageTitle() {
    return this._subpageTitle;
  }

  set subpageTitle(val: string) {
    this._subpageTitle = val;
    this.updateMetaTitle();
  }

  private _subpageIndexing = true;

  get subpageIndexing() {
    return this._subpageIndexing;
  }

  set subpageIndexing(val: boolean) {
    this._subpageIndexing = val;
    this.updateMetaIndexing();
  }

  private _subpageMetaDescription = '';

  get subpageMetaDescription() {
    return this._subpageMetaDescription;
  }

  set subpageMetaDescription(val: string) {
    this._subpageMetaDescription = val;
    this.updateMetaDescription();
  }

  private _subpageMetaKeywords = [];

  get subpageMetaKeywords(): string[] {
    return this._subpageMetaKeywords;
  }

  set subpageMetaKeywords(val: string[]) {
    this._subpageMetaKeywords = val;
    this.updateMetaKeywords();
  }

  private _subpageOgImage?: string;

  get subpageOgImage(): string {
    return this._subpageOgImage;
  }

  set subpageOgImage(val: MediaInterface | string | null) {
    this._subpageOgImage = typeof val === 'string'
      ? val
      : (val?.urls.full || '');
    this.updateOgImage();
  }

  set favicon(val: MediaInterface | string) {
    if (!val) {
      return;
    }

    let url: string;
    if (typeof val === 'string') {
      url = val;
    } else {
      // Obsługuj zarówno camelCase jak i snake_case
      url = val.urls.full || (val as any).full_url || '';
    }

    this._document.getElementById('appFavicon')?.setAttribute('href', url);
  }

  public setSubPageMetaData(page: Page | MetaTagsInfoInterface) {
    if (isPlatformBrowser(this.platformId)) {
      const p = page as unknown as Record<string, unknown>;
      const hrefZLinkuWDom = this._document
        .querySelector('link[rel="canonical"]')
        ?.getAttribute('href');
      const ogUrlMeta = this._document.querySelector('meta[property="og:url"]')?.getAttribute('content');
      console.log('[canonical] przejście na podstronę', {
        pathname: this._document.location.pathname,
        zOdpowiedziApi: {
          canonical: p['canonical'] ?? null,
          canonicalUrl: p['canonicalUrl'] ?? null,
          canonical_url: p['canonical_url'] ?? null,
        },
        wDomLinkRelCanonical: hrefZLinkuWDom ?? null,
        wDomMetaOgUrl: ogUrlMeta ?? null,
      });
    }

    this.applyPageHeaderScript(page);

    this.subpageTitle = page.title || '';
    this.subpageMetaKeywords = page.metaTags || [];
    this.subpageMetaDescription = page.metaDescription || '';
    this.subpageIndexing = page.isIndexed;

    this.subpageOgImage = page.ogImage;
    this.setLocaleLocalStorage(page);
    this.updateOgLocaleTag(page);
    this.updateOgUrlTag();
    // Klient nie synchronizuje canonicalu z API — po wejściu na podstronę (SPA) zdejmujemy przestarzały tag z SSR.
    if (isPlatformBrowser(this.platformId)) {
      this.removeCanonicalLink();
    }
  }

  private applyPageHeaderScript(page: Page | MetaTagsInfoInterface): void {
    const pageHeaderScript = page && Object.hasOwn(page, 'pageHeaderScript')
      ? (page as Page).pageHeaderScript
      : '';

    this.pageHeaderScriptService.apply(pageHeaderScript || '');
  }

  public setLocaleLocalStorage(page: Page | MetaTagsInfoInterface) {
    this.storageService.removeItem('language');
    this.storageService.setItem('language', page.locale);

  }

  public setGlobalMetaData(websiteSettings: WebsiteSettings | MetaTagsInfoInterface) {
    this.favicon = websiteSettings.favicon;
    this.updateOgUrlTag();
  }

  private updateOgImage() {
    const image = this.subpageOgImage;
    this.meta.removeTag('property="og:image"');
    this.meta.addTag({property: 'og:image', content: image});
  }

  private updateOgUrlTag() {
    const url = this._document.location.href;
    this.meta.removeTag('property="og:url"');
    this.meta.addTag({property: 'og:url', content: url});
  }

  private removeCanonicalLink(): void {
    this._document.querySelector('link[rel="canonical"]')?.remove();
  }

  public setStructuredData(json?: string | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    // Zdejmij wszystkie skrypty ld+json (SSR-owy bez klasy oraz poprzedni client-side),
    // by po miękkiej nawigacji nie został przestarzały schemat ze strony głównej.
    this._document
      .querySelectorAll('script[type="application/ld+json"]')
      .forEach((el) => el.remove());

    if (!json) {
      return;
    }
    const script = this._document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = json;
    script.classList.add(this.jsonLdMarkerClass);
    this._document.head.appendChild(script);
  }

  private updateOgLocaleTag(page: Page | MetaTagsInfoInterface) {
    const locale = page.locale;
    this._document.documentElement.lang = locale;
    this.meta.removeTag('property="og:locale"');
    this.meta.addTag({property: 'og:locale', content: locale});
  }

  private updateMetaTitle() {
    const title = this.subpageTitle;
    this.titleService.setTitle(title);
    this.meta.removeTag('property="og:title"');
    this.meta.removeTag('property="og:image:alt"');
    this.meta.addTag({property: 'og:title', content: title});
    this.meta.addTag({property: 'og:image:alt', content: title});
  }

  private updateMetaIndexing() {
    const indexing = this.subpageIndexing;

    this.meta.removeTag('name="robots"');
    this.meta.removeTag('name="googlebot"');

    if (!indexing) {
      this.meta.addTag({name: 'robots', content: 'noindex'});
      this.meta.addTag({name: 'googlebot', content: 'noindex'});
    }
  }

  private updateMetaKeywords() {
    const metaKeywords = this.subpageMetaKeywords.join(', ');

    this.meta.removeTag('name="keywords"');
    if (metaKeywords) {
      this.meta.addTag({name: 'keywords', content: metaKeywords});
    }
  }

  private updateMetaDescription() {
    const metaDescription = this.subpageMetaDescription;

    this.meta.removeTag('property="og:description"');
    this.meta.removeTag('name="description"');
    if (metaDescription) {
      this.meta.addTag({property: 'og:description', content: metaDescription});
      this.meta.addTag({name: 'description', content: metaDescription});
    }
  }

}
